import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { Header } from '@/components/header/Header'
import { SearchField } from '@/components/search-field/SearchField'
import { kUnavailableMemberDisplayName } from '@/collaboration/utils/members'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { selectActiveMatter } from '@/matter/store/selectors'
import { MatterType, type MatterRead } from '@/matter/store/schemas'
import { formatMatterActivityTime } from '@/matter/utils/format-activity-time'
import { kRouteSegments, matterPaths } from '@/routing/paths'
import { useAppSelector } from '@/store/store-hooks'
import { useChatsList } from '../hooks/useChatsList'
import AgentV2ConversationDialog from './AgentV2ConversationDialog'
import { ChatsList } from './ChatsList'

export default function ChatsPage() {
  const matter = useAppSelector(selectActiveMatter)
  return matter ? <MatterChatsPage key={matter.id} matter={matter} /> : null
}

function MatterChatsPage({ matter }: { matter: MatterRead }) {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const list = useChatsList(matter.id)
  const searchRef = useRef<HTMLInputElement>(null)
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const [dialog, setDialog] = useState<{ id: string; kind: 'rename' | 'delete' } | null>(null)
  const rows = list.rows.map((conversation) => ({
    id: conversation.id,
    title: conversation.title,
    owner: conversation.created_by_user_id === list.currentUserId ? 'You' : conversation.creator_display_name || kUnavailableMemberDisplayName,
    href: buildConversationPath(matter.id, conversation.id, kRouteSegments.agentV2),
    activityLabel: formatMatterActivityTime(Date.parse(conversation.updated_at)),
    updatedAt: conversation.updated_at,
  }))

  return (
    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col">
      <Header
        breadcrumbs={[
          {
            id: matter.id,
            label: matter.name,
            onClick: matter.type === MatterType.OUTLOOK_WORKSPACE ? undefined : () => navigate(matterPaths.overview(matter.id)),
          },
          { id: 'chats', label: 'Chats' },
        ]}
      />
      <div ref={setScrollElement} role="region" aria-label="Chat results" className="min-h-0 flex-1 overflow-y-auto px-4">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 pb-8 pt-6">
          <div className="flex flex-col gap-4">
            {/* Below desktop the header's breadcrumb already names the page, so the heading is kept for screen readers only. */}
            <h1 className="font-rethink-sans text-xl font-semibold leading-7 text-brand-neutral-950 max-lg:sr-only">Chats</h1>
            <SearchField ref={searchRef} value={list.searchQuery} onChange={list.setSearchQuery} placeholder="Search chats" aria-label="Search chats" />
          </div>
          <ChatsList
            scrollElement={scrollElement}
            rows={rows}
            isLoading={list.isLoading}
            isError={list.isError}
            searchQuery={list.searchQuery}
            sortDirection={list.sortDirection}
            onSort={list.toggleSort}
            onRetry={list.retry}
            onClearSearch={() => {
              list.setSearchQuery('')
              searchRef.current?.focus()
            }}
            onOpen={() => trackEvent(AnalyticsEvent.OpenExistingConversation)}
            onRename={(id) => setDialog({ id, kind: 'rename' })}
            onDelete={(id) => setDialog({ id, kind: 'delete' })}
          />
        </div>
      </div>
      <AgentV2ConversationDialog conversationId={dialog?.id ?? null} kind={dialog?.kind ?? 'rename'} open={dialog !== null} onClose={() => setDialog(null)} />
    </div>
  )
}
